import { useEffect, useState } from "react";
import config from "../../config";
import { getUserGroups } from "../../utils/cognito";
import { useHistory, useRouteMatch } from "react-router-dom";
import { parseQueryString } from "../../utils/paths";
import { stringify } from "query-string";
import { Md5 } from "ts-md5/dist/md5";

/* Permission Related Utilities Start */
// TODO: the plan is for this value to be available on the text itself
// for now going to hard code the list of bibles that we want to enable...
export const isTranslationInProgress = ({
  dblId
}: {
  dblId: string;
}): boolean => {
  const enabledDblIds = ["6bdd4e7255bde462"];
  return enabledDblIds.some(id => id === dblId);
};

// TODO: needs to be config flag plus... something? maybe a flag in the site config
export const isFeedbackEnabled = (): boolean => {
  return config.featureFlags.translationFeedback;
};

export const canUserGiveFeedback = async ({
  dblId
}: {
  dblId: string;
}): Promise<boolean> => {
  const userGroups = await getUserGroups();
  const userHasPermission = userGroups.some(group => group.includes(dblId));
  return userHasPermission;
};

export const isTranslationFeedbackEnabled = async ({
  dblId
}: {
  dblId: string;
}): Promise<boolean> => {
  const allowedToGiveFeedback = await canUserGiveFeedback({ dblId });

  return (
    isTranslationInProgress({ dblId }) &&
    isFeedbackEnabled() &&
    allowedToGiveFeedback
  );
};

export const useFeedbackStatus = ({ dblId }: { dblId: string }) => {
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState(false);
  const { feedbackVerse } = useFeedbackRouting();

  useEffect(() => {
    isTranslationFeedbackEnabled({ dblId })
      .then(isEnabled => setEnabled(isEnabled))
      .catch(err => {
        console.error(err);
        setError(true);
      });
  }, [dblId]);

  return { enabled, visible: Boolean(feedbackVerse), error };
};
/* Permission Related Utilities End */

/* Feedback Router Releated Utilities Start */
interface UseFeedbackRoutingResult {
  dblId: string;
  bibleId: string;
  feedbackVerse: string | undefined;
  setFeedbackVerse: (verseId?: string) => void;
}

export const useFeedbackRouting = (): UseFeedbackRoutingResult => {
  const { push, location } = useHistory();
  const queryParams = parseQueryString(location.search);

  let dblId = "";
  let bibleId = "";
  const match = useRouteMatch<{ bibleId?: string }>("/bible/:bibleId");
  if (match && match.params && match.params.bibleId) {
    bibleId = match.params.bibleId;
    dblId = bibleId ? bibleId.split("-")[0] : "";
  }

  const setFeedbackVerse = (verseId: string | undefined) => {
    const { feedback: oldFeedback, ...newQueryParams } = queryParams;
    if (typeof verseId === "string") newQueryParams.feedback = verseId;
    push(`${location.pathname}?${stringify(newQueryParams)}`);
  };

  const { feedback } = queryParams;

  let feedbackVerse;
  if (!feedback) {
  } else if (typeof feedback === "object") {
    feedbackVerse = feedback[0];
  } else {
    feedbackVerse = feedback;
  }

  return {
    dblId,
    bibleId,
    feedbackVerse,
    setFeedbackVerse
  };
};
/* Feedback Router Releated Utilities End */

interface GetFeedbackedVersesParams {
  dblId: string;
  chapterId: string;
  verseIds?: string[];
}
export const getFeedbackedVerses = async ({
  dblId,
  chapterId,
  verseIds = []
}: GetFeedbackedVersesParams): Promise<string[]> => {
  const feedbackEnabled = await isTranslationFeedbackEnabled({ dblId });

  if (!feedbackEnabled) return [];

  const { TranslationFeedback } = await import("../../api/TranslationFeedback");
  const tfClient = new TranslationFeedback();
  await tfClient.init({ dblId });

  const channelIds = verseIds.map(
    vId => `${dblId}.${chapterId.replace(".", "_")}.${vId}`
  );

  let feedbackedVerses;
  try {
    const result = await tfClient.fetchMultiFeedback({ channelIds, count: 1 });
    feedbackedVerses = Object.keys(result).map(channelId => {
      const [, channelChapterId, verseId] = channelId.split(".");
      const [bookId, chapterNumber] = channelChapterId.split("_");
      return `${bookId}.${chapterNumber}.${verseId}`;
    });
  } catch (err) {
    console.error(err);
    return [];
  }

  return feedbackedVerses;
};

// TODO: would be ideal to pass in email address, but not messing with this for now...
export const getProfileImageUrl = ({
  userId = "",
  fallback = "identicon",
  size = 64
}: {
  userId: string;
  fallback?: string;
  size?: number;
}) => {
  const hash = Md5.hashStr(userId);
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=${fallback}`;
};

export const channelIdToScriptureRef = (channelId: string) => {
  const [, bookChapterChannelId, verse] = channelId.split(".");
  const [book, chapter] = bookChapterChannelId.split("_");
  return `${book} ${chapter}:${verse}`;
};

export const isInViewport = function(elem: HTMLElement | null) {
  if (elem === null) return false;

  var bounding = elem.getBoundingClientRect();

  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    bounding.right <=
      (window.innerWidth || document.documentElement.clientWidth)
  );
};

export const createThrottledScrollListener = (
  listener: () => void
): (() => void) => {
  let ticking = false;

  return () => {
    if (!ticking) {
      window.requestAnimationFrame(function() {
        listener();
        ticking = false;
      });

      ticking = true;
    }
  };
};
